import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ChevronDown, ArrowRight, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';

const faqCategories = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What age groups do you serve?',
        a: 'Ready Set STEM offers programs for children ages 5-18 (grades K-12). Our curriculum is structured in four levels: Explorer (ages 5-7), Builder (ages 8-10), Coder (ages 11-13), and Innovator (ages 14-18). Each level features age-appropriate projects, languages, and challenges.',
      },
      {
        q: 'Do students need prior coding experience?',
        a: 'No experience is needed! Our Explorer and Builder levels start from absolute zero. We use visual block programming (Scratch, Scratch Jr.) for younger students before transitioning to text-based languages like Python and JavaScript. Every student progresses at their own pace.',
      },
      {
        q: 'How do I enroll my child?',
        a: 'You can create a free parent account on our platform, browse our course catalog, and enroll directly online. For families who qualify for free programs through APS Training Academy (our 501(c)(3) non-profit partner), you can apply through the Community Impact page. We also offer a free trial class — just contact us to schedule one.',
      },
      {
        q: 'What equipment does my child need?',
        a: 'For most classes, students just need a laptop or Chromebook. We provide all robotics kits, STEM materials, and software licenses. For our in-person classes, everything is available at our learning centers. For virtual classes, we can ship STEM kits directly to your home.',
      },
    ],
  },
  {
    category: 'Programs & Curriculum',
    questions: [
      {
        q: 'What subjects do you teach?',
        a: 'Our STEM.org-accredited curriculum spans 10+ subjects: Coding & Programming (Python, Java, JavaScript, Scratch, C++), CAD & 3D Design, AI & Machine Learning, Robotics & IoT, Cybersecurity, Data Science, Game Development (Unity, Scratch), Web & Mobile Development, AR/VR Experiences, and Digital Confidence. This is one of the broadest STEM curricula available for K-12 students.',
      },
      {
        q: 'What is STEM.org accreditation and why does it matter?',
        a: 'STEM.org is the premier accreditation body for STEM education programs. Ready Set STEM has earned dual recognition: the Best In STEM™ award and the Accredited™ Trustmark — placing us in the top 5% of all STEM education programs worldwide. This means our curriculum, instructors, and learning outcomes have been independently verified to meet the highest standards of quality, rigor, and integrity.',
      },
      {
        q: 'How is Ready Set STEM different from Code Ninjas, iCode, or other coding schools?',
        a: 'Several key differentiators: (1) We\'re STEM.org Best In STEM™ — Top 5% worldwide. Most competitors aren\'t accredited. (2) We\'re powered by a 501(c)(3) non-profit that provides free classes to underserved families. (3) We teach 10+ STEM subjects, not just coding. (4) We offer competitive FIRST and VEX robotics teams. (5) Our gamification includes age-gated rewards (Pokémon cards, Roblox gift cards, Fortnite V-Bucks, art supplies, Squishmallows). (6) We serve the most diverse student population in the industry — 49% female/non-binary, 54% Latino.',
      },
      {
        q: 'What is the Robotics Division?',
        a: 'We run competitive robotics teams in both FIRST (FLL, FTC) and VEX (IQ, VRC) competitions. Students design, build, and program robots to compete in regional, state, and national tournaments. We currently have 6 active teams with 5+ tournament awards. Team members fill specialized roles: Captain, Driver, Programmer, Builder, CAD Designer, Scout, and Engineering Notebook Lead.',
      },
    ],
  },
  {
    category: 'Pricing & Financial Aid',
    questions: [
      {
        q: 'How much do classes cost?',
        a: 'Course prices vary by level and duration, typically ranging from $29-$50 per session for group classes. We deliberately keep prices below competitors like Code Ninjas ($200-300/month) because our mission is accessibility. Multi-child discounts and sibling discounts are available.',
      },
      {
        q: 'Are free classes really available?',
        a: 'Yes! Through our partnership with APS Training Academy (a registered 501(c)(3) non-profit), we provide completely free STEAM education to families below the Area Median Income. 93% of our learners qualify for and receive free instruction. This includes in-person classes, all materials, and STEM kits.',
      },
      {
        q: 'How do I apply for free programs?',
        a: 'Visit our Community Impact page and complete the application form. Our team reviews applications within 5-7 business days. If your family\'s household income is at or below the Area Median Income for your county, your children qualify for free classes, workshops, and even robotics team participation.',
      },
      {
        q: 'Do you offer scholarships for paid courses?',
        a: 'Yes. Beyond our free APS Training Academy programs, we offer partial scholarships for families who don\'t qualify for fully free programs but still need financial assistance. Contact us to discuss your situation — our goal is that no child is turned away due to cost.',
      },
    ],
  },
  {
    category: 'Classes & Schedule',
    questions: [
      {
        q: 'Are classes in-person or online?',
        a: 'We offer both! In-person classes are held at our learning centers in Aurora and partner locations across the Fox Valley region. We also offer live virtual classes for families who prefer remote learning. All classes are instructor-led with small class sizes (max 12 students).',
      },
      {
        q: 'What is the class size?',
        a: 'We maintain a maximum of 12 students per class to ensure personalized attention. Our student-to-instructor ratio never exceeds 12:1, and many classes have a teaching assistant as well. Robotics teams have 10-15 members with a head coach and assistant coaches.',
      },
      {
        q: 'How long are the courses?',
        a: 'Most courses run 10-16 weeks with weekly sessions of 60-90 minutes. We also offer intensive workshops (2-4 hours), summer camps (week-long, half-day or full-day), and single-session events. Robotics is a full-season commitment (September through April).',
      },
    ],
  },
  {
    category: 'Gamification & Rewards',
    questions: [
      {
        q: 'What is the points and rewards system?',
        a: 'Students earn points through class attendance, assignment completion, helping classmates, perfect attendance streaks, and coding challenge completions. Points can be redeemed in our Rewards Store for real prizes — including Pokémon cards, Roblox/Minecraft/Fortnite gift cards, STEM kits, art supplies, Squishmallows, and more. Rewards are age-gated so younger students see age-appropriate options.',
      },
      {
        q: 'What are coding challenges?',
        a: 'Gaming-themed coding projects that earn bonus points and badges. Examples: "Build a Creature Collector Game" (Pokémon-inspired, Scratch), "Battle Arena: Turn-Based Combat" (Fortnite-inspired, Python), "Voxel World Builder" (Minecraft-inspired, JavaScript/Three.js), "Fashion Design Studio App", and "AI Art Generator". Challenges are themed and age-gated.',
      },
      {
        q: 'What badges can students earn?',
        a: 'We have 27+ badges across categories: Gaming-themed (Bug Catcher, Victory Royale, Master Builder, Diamond Miner), Creative (Digital Artist, UX Designer, Storyteller), Social Impact (Change Maker, Mentor Heart), and Achievement (Speed Runner, XP Maxed, Achievement Unlocked). Badges appear on student profiles and can be shared.',
      },
    ],
  },
  {
    category: 'Safety & Trust',
    questions: [
      {
        q: 'Are your instructors background-checked?',
        a: 'Yes. Every instructor and volunteer undergoes a comprehensive background check before working with students. Our instructors are trained professionals — many with degrees in Computer Science, Education, or Engineering. Volunteers from partner institutions also complete background checks and our instructor training program.',
      },
      {
        q: 'Is Ready Set STEM a legitimate, accredited organization?',
        a: 'Absolutely. We hold dual STEM.org recognition (Best In STEM™ + Accredited™ Trustmark — top 5% worldwide). We\'re powered by APS Training Academy, a registered 501(c)(3) non-profit (EIN: 83-4284332). We partner with multiple school districts, park districts, and libraries across Illinois. We\'ve served 12,000+ learners with a 98% parent satisfaction rate.',
      },
    ],
  },
];

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const toggleItem = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = searchQuery
    ? faqCategories.map(cat => ({
        ...cat,
        questions: cat.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) || q.a.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(cat => cat.questions.length > 0)
    : faqCategories;

  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 gradient-hero overflow-hidden">
        <div className="container-app relative z-10 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Everything parents want to know about Ready Set STEM — from pricing and curriculum to safety and robotics competitions.
          </p>
          <div className="max-w-lg mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              className="pl-12 py-6 text-lg rounded-full bg-white/95 border-0 shadow-lg"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="section-padding">
        <div className="container-app max-w-4xl">
          {filteredCategories.map((category, ci) => (
            <div key={ci} className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-rsc-navy mb-4 flex items-center gap-2">
                <span className="w-2 h-8 bg-rsc-yellow rounded-full" />
                {category.category}
              </h2>
              <div className="space-y-3">
                {category.questions.map((item, qi) => {
                  const key = `${ci}-${qi}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={key} className={`border rounded-xl overflow-hidden transition-colors ${isOpen ? 'border-rsc-blue bg-rsc-blue/5' : 'hover:border-gray-300'}`}>
                      <button
                        onClick={() => toggleItem(key)}
                        className="w-full flex items-center justify-between p-5 text-left"
                      >
                        <span className="font-semibold text-rsc-navy pr-4">{item.q}</span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-rsc-blue' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 text-gray-700 leading-relaxed">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredCategories.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No questions match your search.</p>
              <button onClick={() => setSearchQuery('')} className="text-rsc-blue font-semibold mt-2 hover:underline">Clear search</button>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="section-padding bg-gradient-to-r from-rsc-blue to-rsc-navy text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
            Our team is here to help. Reach out anytime — we'd love to chat about how Ready Set STEM can work for your family.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact"><Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">Contact Us <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/catalog"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Browse Courses</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
